import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Box, IconButton, Text } from '@wix/design-system';
import { ChevronLeftSmall, ChevronRightSmall } from '@wix/wix-ui-icons-common';
import s from './horizontal-nav-bar.module.scss';
import { horizontalNavBarDataHooks } from './data-hooks';
import { useHorizontalScroll } from './use-horizontal-scroll';

export interface HorizontalNavBarItem {
  key: string;
  label: string;
  href?: string;
}

export interface HorizontalNavBarProps {
  items: HorizontalNavBarItem[];
  selectedItemKey?: string;
  onItemClick(item: HorizontalNavBarItem, index: number): void;
  /* rendered at the end of the bar, e.g. a search box */
  sideContent?: React.ReactNode;
}

export function HorizontalNavBar({
  items,
  selectedItemKey,
  onItemClick,
  sideContent,
}: HorizontalNavBarProps) {
  const {
    scrollerRef: itemsRef,
    canScrollBack,
    canScrollForward,
    scrollByChunk,
  } = useHorizontalScroll<HTMLUListElement>(items);

  /* items overflow into horizontal scroll - keep the selected one visible */
  useEffect(() => {
    const selectedItem = itemsRef.current?.querySelector(
      '[aria-current="page"]',
    );
    selectedItem?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [itemsRef, selectedItemKey]);

  return (
    <nav
      className={s.horizontalNavBar}
      data-hook={horizontalNavBarDataHooks.root}
    >
      <Box className={s.itemsContainer}>
        <Box
          className={classNames(s.arrowOverlay, s.arrowOverlayBack, {
            [s.arrowVisible]: canScrollBack,
          })}
        >
          <IconButton
            size="tiny"
            skin="standard"
            priority="secondary"
            ariaLabel="Scroll back"
            dataHook={horizontalNavBarDataHooks.scrollArrow('back')}
            className={s.scrollArrow}
            aria-hidden={!canScrollBack}
            tabIndex={canScrollBack ? 0 : -1}
            data-visible={canScrollBack}
            onClick={() => scrollByChunk(-1)}
          >
            <ChevronLeftSmall />
          </IconButton>
        </Box>
        <ul className={s.items} ref={itemsRef}>
          {items.map((item, index) => {
            const isSelected = item.key === selectedItemKey;
            /* support href on SA, and prevent-default navigation in other containers */
            const PillTag = item.href ? 'a' : 'button';
            return (
              <li key={item.key} className={s.item}>
                <PillTag
                  data-hook={horizontalNavBarDataHooks.item(item.key)}
                  className={classNames(s.pill, { [s.selected]: isSelected })}
                  href={item.href}
                  type={item.href ? undefined : 'button'}
                  aria-current={isSelected ? 'page' : undefined}
                  onClick={(event: React.MouseEvent) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onItemClick(item, index);
                  }}
                >
                  <Text size="small" light={isSelected}>
                    {item.label}
                  </Text>
                </PillTag>
              </li>
            );
          })}
        </ul>
        <Box
          className={classNames(s.arrowOverlay, s.arrowOverlayForward, {
            [s.arrowVisible]: canScrollForward,
          })}
        >
          <IconButton
            size="tiny"
            skin="standard"
            priority="secondary"
            ariaLabel="Scroll forward"
            dataHook={horizontalNavBarDataHooks.scrollArrow('forward')}
            className={s.scrollArrow}
            aria-hidden={!canScrollForward}
            tabIndex={canScrollForward ? 0 : -1}
            data-visible={canScrollForward}
            onClick={() => scrollByChunk(1)}
          >
            <ChevronRightSmall />
          </IconButton>
        </Box>
      </Box>
      {sideContent && (
        <Box
          className={s.sideContent}
          dataHook={horizontalNavBarDataHooks.sideContent}
        >
          {sideContent}
        </Box>
      )}
    </nav>
  );
}
